import { styled, Box, TableContainer, Theme } from '@mui/material';
import { lighten } from '@mui/material/styles';
import { RowSeverity } from './types';

export const TableContainerStyled = styled(TableContainer)(({ theme }) => ({
  width: '100%',
  maxHeight: 'calc(100vh - 300px)', // Fixed height with scroll
  overflowY: 'auto',
  overflowX: 'auto',
  backgroundColor: 'transparent',
  borderRadius: '8px',
  position: 'relative',
  zIndex: 0,
}));

export const StyledTable = styled('table')({
  width: '100%',
  borderCollapse: 'separate',
  borderSpacing: '0 4px',
  fontSize: '14px',
  tableLayout: 'fixed',
});

export const StyledThead = styled('thead')({
  position: 'sticky',
  top: 0,
  zIndex: 10,
  isolation: 'isolate',
  '& tr': {
    backgroundColor: 'transparent',
    filter: 'drop-shadow(0 2px 3px rgba(0, 0, 0, 0.08))',
  },
});

export const StyledTh = styled('th')<{ align?: 'left' | 'center' | 'right' }>(({ align = 'left' }) => ({
  padding: '16px',
  textAlign: align,
  fontWeight: 600,
  fontSize: '14px',
  color: '#616161',
  cursor: 'pointer',
  userSelect: 'none',
  whiteSpace: 'nowrap',
  backgroundColor: '#fff',
  position: 'relative',
  '&:first-of-type': {
    borderTopLeftRadius: '8px',
    borderBottomLeftRadius: '8px',
  },
  '&:last-of-type': {
    borderTopRightRadius: '8px',
    borderBottomRightRadius: '8px',
  },
  '&:hover': {
    backgroundColor: '#f5f5f5',
  },
}));

export const StyledTbody = styled('tbody')({
  position: 'relative',
  zIndex: 1,
});

/**
 * Colours for a flagged row, derived from the matching palette entry so a rebrand of the error or warning
 * colour carries through here too. Adding a severity needs no change in this file.
 *
 * The tint is `lighten`ed rather than alpha-blended on purpose: the cells paint it as their own background,
 * so a translucent tint would pick up whatever sits behind the table instead of a predictable colour.
 */
const rowSeverityColours = (theme: Theme, severity?: RowSeverity) => {
  if (!severity) return null;

  const accent = theme.palette[severity].main;
  return { background: lighten(accent, 0.9), accent };
};

export const StyledTr = styled('tr')<{ isHovered?: boolean; isSelected?: boolean; severity?: RowSeverity }>(
  ({ theme, isHovered, isSelected, severity }) => {
    const colours = rowSeverityColours(theme, severity);
    const rowBackground = (() => {
      if (colours) return colours.background;
      if (isSelected) return '#DBEDE3';
      if (isHovered) return '#f9f9f9';
      return '#fff';
    })();

    return {
      backgroundColor: 'transparent',
      height: '56px',
      // Keeps a row scrolled to from above (scrollToRowId) from ending up beneath the sticky header.
      scrollMarginTop: '56px',
      '& td': {
        backgroundColor: rowBackground,
        transition: 'background-color 0.2s ease-in-out',
      },
      '&:hover td': {
        backgroundColor: colours ? colours.background : '#DBEDE3',
      },
      '& td:first-of-type': {
        borderTopLeftRadius: '8px',
        borderBottomLeftRadius: '8px',
        // The accent has to live on the cell, not the row: cells paint their own background over the row and
        // it is the cell that carries the corner radius, so an accent on the square row edge would leak out
        // past the rounded corners.
        boxShadow: colours ? `inset 3px 0 0 ${colours.accent}` : 'none',
      },
      '& td:last-of-type': {
        borderTopRightRadius: '8px',
        borderBottomRightRadius: '8px',
      },
    };
  },
);

export const StyledTd = styled('td')<{ align?: 'left' | 'center' | 'right' }>(({ align = 'left' }) => ({
  padding: '16px',
  textAlign: align,
  fontSize: '14px',
  color: '#212121',
  verticalAlign: 'middle',
  height: '56px',
  maxHeight: '56px',
  border: 'none',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}));

export const CheckboxCell = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

export const EmptyStateContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '200px',
  padding: '40px',
  color: '#9e9e9e',
  fontSize: '14px',
});

export const SelectionFooterContainer = styled(Box)<{ isVisible?: boolean }>(({ isVisible = true }) => ({
  position: 'sticky',
  bottom: 0,
  left: 0,
  right: 0,
  zIndex: 1,
  backgroundColor: '#fff',
  // borderTop: '2px solid #e0e0e0',
  width: '100%',
  boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.05)',
  maxHeight: isVisible ? '200px' : '0px',
  overflow: 'hidden',
  opacity: isVisible ? 1 : 0,
  transition: 'max-height 0.3s ease-in-out, opacity 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
}));

export const PaginationContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '16px 24px',
  backgroundColor: '#fff',
  borderTop: '1px solid #e0e0e0',
  borderRadius: '0 0 8px 8px',
  marginTop: '8px',
});

export const PaginationInfo = styled(Box)({
  fontSize: '14px',
  color: '#616161',
});

export const PaginationControls = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

// Mobile Card View Styles
export const MobileCardContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  padding: '8px',
  width: '100%',
}));

export const MobileCard = styled(Box)<{ isSelected?: boolean; severity?: RowSeverity }>(
  ({ theme, isSelected, severity }) => {
    const colours = rowSeverityColours(theme, severity);

    // The accent is carried by box-shadow, so any rule that overrides box-shadow for another state has to
    // re-compose it � otherwise the accent vanishes for as long as that state is active.
    const withAccent = (shadow: string) => (colours ? `inset 3px 0 0 ${colours.accent}, ${shadow}` : shadow);

    return {
      backgroundColor: (() => {
        if (colours) return colours.background;
        if (isSelected) return '#DBEDE3';
        return '#fff';
      })(),
      borderRadius: '8px',
      padding: '16px',
      boxShadow: withAccent('0 1px 3px rgba(0, 0, 0, 0.1)'),
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
      width: '100%',
      transition: 'box-shadow 0.2s ease-in-out, background-color 0.2s ease-in-out',
      '&:active': {
        boxShadow: withAccent('0 2px 8px rgba(0, 0, 0, 0.15)'),
      },
    };
  },
);

export const MobileCardRow = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  gap: '8px',
  flexWrap: 'wrap',
});

export const MobileCardLabel = styled(Box)(({ theme }) => ({
  fontSize: '12px',
  fontWeight: 600,
  color: '#616161',
  minWidth: '100px',
  flexShrink: 0,
}));

export const MobileCardValue = styled(Box)({
  fontSize: '14px',
  color: '#212121',
  flex: 1,
  wordBreak: 'break-word',
});

export const MobileCardActions = styled(Box)({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '12px',
  marginTop: '8px',
  paddingTop: '12px',
  borderTop: '1px solid #e0e0e0',
});
